// src/components/Header.tsx
import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ChevronRight, 
  ChevronLeft,
  X, 
  Search, 
  Filter, 
  FileText, 
  Info, 
  BookOpen, 
  ArrowLeft, 
  Menu, 
  Layers,
  HelpCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import SearchBar from "./SearchBar";
import InfoDialog from "@/components/InfoDialog";
import FilterPanel from "./FilterPanel";
import DocumentListButton from "./DocumentListButton";
import QuizButton from "./QuizButton";
import FullscreenButton from "./FullscreenButton";
import { ProcessItem, EnhancedDocumentLink, FilterState } from "@/types";

interface HeaderProps {
  searchTerm: string;
  searchResults: ProcessItem[];
  onSearchChange: (term: string) => void;
  onClearSearch: () => void;
  onResultSelect: (
    item: ProcessItem,
    focusType?: "document" | "participant" | "description"
  ) => void;
  processData: ProcessItem[];
  onFilterChange: (filters: FilterState) => void;
  onShowDocumentList: (documents: EnhancedDocumentLink[]) => void;
  onStartTutorial: () => void;
  onShowQuiz: () => void;
  businessArea?: string | null;
  category?: string | null;
  onBackToAreas?: () => void;
  onBackToCategories?: () => void;
}

const Header: React.FC<HeaderProps> = ({
  searchTerm,
  searchResults,
  onSearchChange,
  onClearSearch,
  onResultSelect,
  processData,
  onFilterChange,
  onShowDocumentList,
  onStartTutorial,
  onShowQuiz,
  businessArea,
  category,
  onBackToAreas,
  onBackToCategories,
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);

  // Referens för sökbaren
  const searchBarRef = useRef<HTMLDivElement>(null);

  // Beräkna scrollbar-bredden vid komponentmontering och vid fönsterändring
  useEffect(() => {
    const calculateScrollbarWidth = () => {
      // Skillnad mellan fönstrets innerWidth och dokumentets width är scrollbar-bredden
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      setScrollbarWidth(scrollbarWidth);
    };

    // Kör vid start
    calculateScrollbarWidth();

    // Lägg till en resize-lyssnare för att uppdatera vid fönsterändring
    window.addEventListener('resize', calculateScrollbarWidth);
    
    // Cleanup
    return () => window.removeEventListener('resize', calculateScrollbarWidth);
  }, []);

  // Lyssna på scroll
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Stäng mobilmeny om fönstret blir desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
        setIsFilterModalOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Klick utanför mobilmenyn
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isMobileMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Element;
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(target) &&
        !target.closest("#menu-button")
      ) {
        setIsMobileMenuOpen(false);
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMobileMenuOpen]);

  // Klick utanför sökrutan
  useEffect(() => {
    if (!isSearchExpanded) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Element;
      if (
        searchBarRef.current &&
        !searchBarRef.current.contains(target) &&
        !target.closest(".search-related")
      ) {
        setIsSearchExpanded(false);
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isSearchExpanded]);

  // ESC stänger mobilmeny, sökruta och filtermodal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsMobileMenuOpen(false);
        setIsSearchExpanded(false);
        setIsFilterModalOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isMobileMenuOpen, isSearchExpanded, isFilterModalOpen]);

  // Back-knapp (om onBackToAreas finns)
  const BackButton = () => {
    // If both back functions are available, prioritize back to categories
    const handleBack = () => {
      if (category && onBackToCategories) {
        onBackToCategories();
      } else if (onBackToAreas) {
        onBackToAreas();
      }
    };

    // Only show back button if we have somewhere to go back to
    if (!onBackToAreas && !onBackToCategories) return null;

    return (
      <Button
        variant="outline"
        onClick={handleBack}
        className="absolute left-4 top-4 bg-gray-800/70 text-orange-300 border border-orange-500/30 hover:bg-orange-500/20 hover:text-orange-400 rounded-full p-2 h-10 w-10 flex items-center justify-center shadow-md z-50 transition-all"
        aria-label={category && onBackToCategories ? "Tillbaka till kategorier" : "Tillbaka till affärsområden"}
      >
        <ArrowLeft className="h-5 w-5" />
      </Button>
    );
  };

  // Hämta alla dokument
  const handleShowAllDocuments = () => {
    const allDocs = processData
      .flatMap((item) => {
        if (!item.field_4) return [];
        return item.field_4.split(";").map((docStr) => {
          const match = docStr.match(/(.*?)\s*\((.*?)\)/);
          if (match && match[1] && match[2]) {
            return {
              title: match[1].trim(),
              url: match[2].trim(),
              process: item.Title,
              subProcess: item.field_0,
              moment: item.field_2,
              hierarchicalNumber: item.hierarchicalNumber,
            };
          }
          return null;
        });
      })
      .filter(Boolean) as any[];
    onShowDocumentList(allDocs);
  };

  // Gemensam stil för knappar i header
  const headerButtonStyle =
    "bg-gray-800/50 text-gray-300 border border-gray-700 hover:bg-orange-300/10 hover:text-orange-300 transition-colors flex items-center justify-center h-9 w-9 p-0";

  // Gemensam stil för knappar i mobilmenyn
  const mobileMenuButtonStyle = 
    "w-full bg-gray-800/70 text-gray-200 border border-gray-700 hover:bg-orange-300/10 hover:text-orange-300 flex items-center justify-start gap-2 px-3 py-2 rounded-md";

  const headerStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: `${scrollbarWidth}px`, // Här justerar vi för scrollbarens bredd
    zIndex: 50,
    width: `calc(100% - ${scrollbarWidth}px)`,
    transition: 'all 0.3s',
  } as React.CSSProperties;

  return (
    <>
      <header
        style={headerStyle}
        className={`
          transition-all duration-300 header-nav 
          ${isScrolled
            ? "bg-gray-900/95 shadow-md backdrop-blur-md border-b border-gray-800/50"
            : "bg-slate-800/80 backdrop-blur-sm"
          }
        `}
      >
        <BackButton />

        <div className="container mx-auto px-4 py-3 relative">
          {/* Desktop-läge */}
          <div className="hidden md:flex md:items-center md:justify-between">
            {/* Vänster: Rubrik + affärsområde + kategori */}
            <div className="flex flex-col">
              <h1 className="text-xl font-semibold text-white">
                Process Viewer
              </h1>
              <div className="flex items-center gap-2 mt-1">
                {businessArea && (
                  <div className="bg-orange-500/10 text-orange-300 px-2 py-1 rounded text-xs font-medium border border-orange-500/20 flex items-center">
                    {businessArea}
                    {category && (
                      <>
                        <ChevronRight className="h-3 w-3 mx-1 text-orange-500/70" />
                        <span className="font-semibold">{category}</span>
                      </>
                    )}
                  </div>
                )}
                {category && onBackToCategories && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onBackToCategories}
                    className="h-6 px-2 text-xs text-gray-400 hover:text-orange-300"
                  >
                    <ChevronLeft className="h-3 w-3 mr-1" />
                    <span>Kategorier</span>
                  </Button>
                )}
              </div>
            </div>

            {/* Mitten: Sökfält */}
            <div 
              className="flex items-center justify-center search-container"
              id="search-container"
              data-tutorial-target="search"
            >
              <SearchBar
                searchTerm={searchTerm}
                searchResults={searchResults}
                onSearchChange={onSearchChange}
                onClear={onClearSearch}
                onResultSelect={onResultSelect}
                className="tutorial-search-bar search-bar"
              />
            </div>

            {/* Höger: Knappar */}
            <div 
              className="flex items-center gap-3 relative z-10 header-tools"
              id="header-tools"
              data-tutorial-target="tools"
            >
              <div className="hidden md:block">
                <FilterPanel
                  processData={processData}
                  onFilterChange={(filters) => {
                    onFilterChange(filters);
                  }}
                  iconsOnly={true}
                />
              </div>

              {/* Dokument-knapp */}
              <DocumentListButton
                processData={processData}
                onShowDocumentList={onShowDocumentList}
                iconsOnly
              />

              {/* Quiz-knapp */}
              <QuizButton onClick={onShowQuiz} iconsOnly />

              {/* Guide-knapp */}
              <Button
                variant="outline"
                size="icon"
                className={`${headerButtonStyle} transition-transform hover:scale-110 guide-button`}
                onClick={onStartTutorial}
                aria-label="Guide"
                data-tutorial-target="guide"
              >
                <HelpCircle className="h-4 w-4" />
              </Button>

              {/* Info-knapp */}
              <InfoDialog
                trigger={
                  <Button
                    variant="outline"
                    size="icon"
                    className={`${headerButtonStyle} transition-transform hover:scale-110 info-button`}
                    aria-label="Information"
                    data-tutorial-target="info"
                  >
                    <Info className="h-4 w-4" />
                  </Button>
                }
              />

              {/* Fullskärm-knapp */}
              <FullscreenButton iconsOnly />
            </div>
          </div>

          {/* Mobil-läge */}
          <div className="flex md:hidden items-center justify-between">
            {/* Rubrik + affärsområde (centrerad) */}
            <div className="flex-1" />
            <div className="flex flex-col items-center">
              <h1 className="text-lg font-semibold text-white">
                Process Navigator
              </h1>
              {businessArea && (
                <div className="mt-1 bg-orange-500/10 text-orange-300 px-2 py-1 rounded text-xs font-medium border border-orange-500/20 w-fit flex items-center">
                  {businessArea}
                  {category && (
                    <>
                      <ChevronRight className="h-3 w-3 mx-1 text-orange-500/70" />
                      <span className="font-semibold">{category}</span>
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="flex-1 flex justify-end">
              {/* Hamburgermeny-knapp */}
              <Button
                id="menu-button"
                variant="ghost"
                size="sm"
                className="px-2 text-orange-300 hover:text-orange-400 hover:bg-orange-400/10"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label={isMobileMenuOpen ? "Stäng meny" : "Öppna meny"}
              >
                {isMobileMenuOpen ? (
                  <X className="h-5 w-5" />
                ) : (
                  <Menu className="h-5 w-5" />
                )}
              </Button>
            </div>
          </div>

          {/* Mobilmenyn – utökad med fler alternativ */}
          <AnimatePresence>
            {isMobileMenuOpen && (
              <motion.div
                id="mobile-menu"
                ref={mobileMenuRef}
                className="md:hidden pt-3 pb-2 border-t border-gray-800 mt-3 bg-gray-900/95 rounded-b-lg shadow-lg"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                <div className="px-2 flex flex-col gap-2">
                  {/* Öppna sök */}
                  <Button
                    variant="outline"
                    className={`search-related ${mobileMenuButtonStyle}`}
                    onClick={() => {
                      setIsSearchExpanded(true);
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    <Search className="h-4 w-4" />
                    <span>Sök</span>
                  </Button>

                  {/* Dokument */}
                  <Button
                    variant="outline"
                    className={mobileMenuButtonStyle}
                    onClick={() => {
                      handleShowAllDocuments();
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    <FileText className="h-4 w-4" />
                    <span>Dokument</span>
                  </Button>

                  {/* Filter */}
                  <Button
                    variant="outline"
                    className={mobileMenuButtonStyle}
                    onClick={() => {
                      setIsFilterModalOpen(true);
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    <Filter className="h-4 w-4" />
                    <span>Filter</span>
                  </Button>

                  {/* Quiz */}
                  <Button
                    variant="outline"
                    className={mobileMenuButtonStyle}
                    onClick={() => {
                      onShowQuiz();
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    <BookOpen className="h-4 w-4" />
                    <span>Processquiz</span>
                  </Button>

                  {/* Guide */}
                  <Button
                    variant="outline"
                    className={mobileMenuButtonStyle}
                    onClick={() => {
                      onStartTutorial();
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    <HelpCircle className="h-4 w-4" />
                    <span>Guide</span>
                  </Button>

                  {/* Tillbaka till kategorier */}
                  {category && onBackToCategories && (
                    <Button
                      variant="outline"
                      className={`${mobileMenuButtonStyle} bg-orange-500/10 border-orange-500/30`}
                      onClick={() => {
                        onBackToCategories();
                        setIsMobileMenuOpen(false);
                      }}
                    >
                      <Layers className="h-4 w-4" />
                      <span>Tillbaka till kategorier</span>
                    </Button>
                  )}

                  {/* Tillbaka till affärsområden */}
                  {onBackToAreas && (
                    <Button
                      variant="outline"
                      className={`${mobileMenuButtonStyle} mt-2`}
                      onClick={() => {
                        onBackToAreas();
                        setIsMobileMenuOpen(false);
                      }}
                    >
                      <ArrowLeft className="h-4 w-4" />
                      <span>Tillbaka till affärsområden</span>
                    </Button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Mobil: expanderad sök */}
        <AnimatePresence>
          {isSearchExpanded && window.innerWidth < 768 && (
            <motion.div
              key="mobile-search-panel"
              ref={searchBarRef}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="w-full bg-gray-900/90 border-t border-b border-gray-800/50"
            >
              <div className="container mx-auto px-4 py-3 flex items-center">
                <Search className="h-4 w-4 text-gray-400 mr-2 search-related" />
                <div className="flex-1 search-related">
                  <SearchBar
                    searchTerm={searchTerm}
                    searchResults={searchResults}
                    onSearchChange={onSearchChange}
                    onClear={() => {
                      onClearSearch();
                      setIsSearchExpanded(false);
                    }}
                    onResultSelect={(item, focusType) => {
                      onResultSelect(item, focusType);
                      setIsSearchExpanded(false);
                    }}
                    className="search-bar"
                  />
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-gray-400 hover:text-white p-2 search-related"
                  onClick={() => setIsSearchExpanded(false)}
                  aria-label="Stäng sök"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Filterpanel som modal i mobilläge */}
        <AnimatePresence>
          {isFilterModalOpen && window.innerWidth < 768 && (
            <motion.div
              key="filter-modal"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
              onClick={() => setIsFilterModalOpen(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="bg-gray-900 border border-gray-800 rounded-lg w-full max-w-md max-h-[80vh] overflow-y-auto custom-scrollbar shadow-xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-4 border-b border-gray-800 flex justify-between items-center">
                  <div className="flex items-center text-orange-300 font-medium">
                    <Filter className="h-4 w-4 mr-2" />
                    <span>Filtrera processer</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-gray-400 hover:text-white"
                    onClick={() => setIsFilterModalOpen(false)}
                    aria-label="Stäng filter"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <div className="p-4">
                  <FilterPanel
                    processData={processData}
                    onFilterChange={onFilterChange}
                    iconsOnly={false}
                  />
                </div>
                <div className="p-4 border-t border-gray-800 flex justify-end">
                  <Button
                    variant="outline"
                    className="bg-orange-500/10 text-orange-300 border-orange-500/30 hover:bg-orange-500/20"
                    onClick={() => setIsFilterModalOpen(false)}
                  >
                    Klar
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  );
};

export default Header;
